/**
 * StepExperience — add 1-3 professional experiences.
 *
 * Each experience card has fields + a "🪄 Structurer" button that calls
 * /api/ai?route=structure&type=experience to reformat free text into ATS bullets.
 */

import { useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { StructureButton } from '../StructureButton';
import type { CVData, Experience } from '../../../types';

interface StepExperienceProps {
  data: CVData;
  updateExperience: (index: number, field: keyof Experience, value: string) => void;
  addExperience: () => void;
  removeExperience: (index: number) => void;
}

export function StepExperience({
  data,
  updateExperience,
  addExperience,
  removeExperience,
}: StepExperienceProps) {
  const experiences = data.experience;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Tes expériences</h1>
        <p className="text-sm text-white/60">
          Ajoute tes postes les plus récents. L'IA peut reformater ta description
          en bullet points ATS-optimisés.
        </p>
      </div>

      <div className="space-y-4">
        {experiences.map((exp, i) => (
          <ExperienceCard
            key={i}
            index={i}
            exp={exp}
            canRemove={experiences.length > 1}
            onUpdate={updateExperience}
            onRemove={removeExperience}
          />
        ))}
      </div>

      {experiences.length < 4 && (
        <button
          type="button"
          onClick={addExperience}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-dashed border-white/20 text-white/50 hover:border-white/40 hover:text-white/70 transition-all w-full justify-center"
        >
          <Plus className="size-4" />
          Ajouter une expérience
        </button>
      )}

      <p className="text-xs text-white/30">
        Tu pourras ajouter d'autres expériences depuis l'éditeur après le wizard.
      </p>
    </div>
  );
}

function ExperienceCard({
  index,
  exp,
  canRemove,
  onUpdate,
  onRemove,
}: {
  index: number;
  exp: Experience;
  canRemove: boolean;
  onUpdate: (i: number, f: keyof Experience, v: string) => void;
  onRemove: (i: number) => void;
}) {
  const savedDescRef = useRef(exp.description);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-white/40 uppercase tracking-wider">
          Expérience {index + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="size-3.5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor={`wiz-exp-pos-${index}`} className="text-xs text-white/70">Titre du poste</Label>
          <Input
            id={`wiz-exp-pos-${index}`}
            type="text"
            value={exp.position}
            onChange={(e) => onUpdate(index, 'position', e.target.value)}
            placeholder="Développeur Full-Stack"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`wiz-exp-co-${index}`} className="text-xs text-white/70">Entreprise</Label>
          <Input
            id={`wiz-exp-co-${index}`}
            type="text"
            value={exp.company}
            onChange={(e) => onUpdate(index, 'company', e.target.value)}
            placeholder="Acme Corp"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`wiz-exp-start-${index}`} className="text-xs text-white/70">Début</Label>
          <Input
            id={`wiz-exp-start-${index}`}
            type="text"
            value={exp.startDate}
            onChange={(e) => onUpdate(index, 'startDate', e.target.value)}
            placeholder="Jan 2022"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`wiz-exp-end-${index}`} className="text-xs text-white/70">Fin</Label>
          <Input
            id={`wiz-exp-end-${index}`}
            type="text"
            value={exp.endDate}
            onChange={(e) => onUpdate(index, 'endDate', e.target.value)}
            placeholder="Aujourd'hui"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`wiz-exp-desc-${index}`} className="text-xs text-white/70">
          Description (missions, résultats)
        </Label>
        <Textarea
          id={`wiz-exp-desc-${index}`}
          value={exp.description}
          onChange={(e) => onUpdate(index, 'description', e.target.value)}
          placeholder="Décris ce que tu faisais, tes projets, tes résultats… L'IA va le transformer en bullets ATS."
          rows={4}
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm resize-none"
        />
        <StructureButton
          type="experience"
          text={exp.description}
          context={{ position: exp.position, company: exp.company }}
          onBeforeApply={() => { savedDescRef.current = exp.description; }}
          onResult={(res) => {
            if (res.description) onUpdate(index, 'description', res.description);
          }}
          onUndo={() => onUpdate(index, 'description', savedDescRef.current)}
          disabled={!exp.description.trim()}
        />
      </div>
    </div>
  );
}
